import { Injectable, signal, computed, inject } from '@angular/core';
import { PricingApiService } from './pricing-api.service';
import { PricingDisplayProduct, PricingDisplayRegion } from '../models';

/**
 * PricingStoreService centralizes pricing state for the app.
 * Manages products, loading, error, and selection using Angular signals.
 * Components can inject this service to read signals or trigger actions.
 */
@Injectable({ providedIn: 'root' })
export class PricingStoreService {
  /** Loading state while fetching products. */
  readonly loading = signal<boolean>(true);

  /** Error message from last failed operation. */
  readonly error = signal<string | null>(null);

  /** All products as returned by the API (sorted by priority). */
  private readonly allProducts = signal<PricingDisplayProduct[]>([]);

  /** Optional allow-lists (embedding hosts); empty means no filtering. */
  readonly productFilter = signal<string[]>([]);
  readonly planFilter = signal<string[]>([]);

  /** Embedding-host preselection and presentation hints (all optional). */
  readonly preselectProductCode = signal<string>('');
  readonly preselectPlanCode = signal<string>('');
  readonly defaultPeriod = signal<'monthly' | 'yearly'>('monthly');
  readonly ctaLabel = signal<string>('');

  /**
   * Current list of display products, narrowed by productFilter/planFilter.
   * Products left without plans by planFilter are dropped.
   */
  readonly products = computed<PricingDisplayProduct[]>(() => {
    const all = this.allProducts();
    const productCodes = this.productFilter();
    const planCodes = this.planFilter();
    if (productCodes.length === 0 && planCodes.length === 0) return all;

    return all
      .filter(p => productCodes.length === 0 || productCodes.includes(p.code))
      .map(p => planCodes.length === 0
        ? p
        : { ...p, plans: p.plans.filter(plan => planCodes.includes(plan.code)) })
      .filter(p => planCodes.length === 0 || p.plans.length > 0);
  });

  /** Plan code to highlight; only when it belongs to the preselected product (if one is set). */
  readonly highlightedPlanCode = computed<string>(() => {
    const plan = this.preselectPlanCode();
    if (!plan) return '';
    const product = this.preselectProductCode();
    return !product || this.selectedProduct()?.code === product ? plan : '';
  });

  /** Selected product code; falls back to first product when undefined. */
  readonly selectedProductCode = signal<string | null>(null);

  /** Selected product derived from products and selected code. */
  readonly selectedProduct = computed<PricingDisplayProduct | null>(() => {
    const items = this.products();
    const code = this.selectedProductCode();
    if (!items || items.length === 0) return null;
    if (code) {
      const found = items.find(p => p.code === code);
      if (found) return found;
    }
    return items[0] ?? null;
  });

  /** Selected region for pricing calculations */
  readonly selectedRegion = signal<PricingDisplayRegion | null>(null);

  /** Selected ranged value (e.g., number of seats/users) for pricing calculations */
  readonly selectedRangedValue = signal<number | undefined>(undefined);

  /** API service for fetching data. */
  private readonly api = inject(PricingApiService);

  constructor() {}

  /**
   * Load products (API returns ALL pricing data).
   * Sets loading/error signals and updates products on success.
   */
  loadProducts(regionCode?: string, rangedValue?: number): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getProducts(regionCode, rangedValue).subscribe({
      next: (products) => {
        // Sort products by priority before storing
        const sortedProducts = [...products].sort((a, b) => {
          const priorityA = a.priority ?? 999;
          const priorityB = b.priority ?? 999;
          return priorityA - priorityB;
        });
        
        this.allProducts.set(sortedProducts);
        // Ensure selection remains valid; prefer the host's preselected product, then the first
        // (sorted by priority) among the visible products
        const visible = this.products();
        if (visible.length > 0) {
          const current = this.selectedProductCode();
          if (!current || !visible.some(p => p.code === current)) {
            const wanted = this.preselectProductCode();
            this.selectedProductCode.set(
              visible.some(p => p.code === wanted) ? wanted : visible[0].code
            );
          }
        } else {
          this.selectedProductCode.set(null);
        }
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load products. Please try again later.');
        this.loading.set(false);
        console.error('[PricingStore] Error loading products:', err);
      }
    });
  }

  /**
   * Select a product by code, if present in the products list.
   */
  selectProductByCode(code: string): void {
    /**
     * Guard against empty codes to prevent overriding selection with undefined.
     */
    if (!code) {
      console.warn('[PricingStore] selectProductByCode: called with empty code');
      return;
    }
    const items = this.products();
    if (!items || items.length === 0) {
      this.selectedProductCode.set(null);
      return;
    }
    this.selectedProductCode.set(code);
  }

  /**
   * Clear products and selection; preserves error/loading as-is.
   */
  clearProducts(): void {
    this.allProducts.set([]);
    this.selectedProductCode.set(null);
  }
}